import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LogIn, User, Car, ClipboardList, ArrowRight, Search, Upload, Check, Wrench, PaintBucket, ShieldCheck } from "lucide-react";
import VinScanner from "@/components/VinScanner";

const TIPOS = ["Particular", "Garantia", "Frota", "Body Shop"];

const JOB_TYPES = [
  { id: "mechanic", label: "Mecânica", icon: Wrench, desc: "Oficina padrão", tipo: "Particular" },
  { id: "body_shop", label: "Body Shop", icon: PaintBucket, desc: "Funilaria/pintura + seguradora", tipo: "Body Shop" },
  { id: "warranty", label: "Garantia Estendida", icon: ShieldCheck, desc: "Reparo coberto por warranty", tipo: "Garantia" },
];

export default function CheckIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get("type"); // mechanic | body_shop | warranty
  const [step, setStep] = useState(1);
  const [jobType, setJobType] = useState(
    JOB_TYPES.find((t) => t.id === preselected)?.id || "mechanic"
  );

  // extra fields for body_shop/warranty
  const [extra, setExtra] = useState({});

  // Pre-fill VIN from URL if provided (from Check Status "not found" flow)
  useEffect(() => {
    const urlVin = searchParams.get("vin");
    if (urlVin && urlVin.length === 17) {
      setNewVehicle((v) => ({ ...v, vin: urlVin.toUpperCase() }));
      setClientMode("new");
      setVehicleMode("new");
    }
    // eslint-disable-next-line
  }, []);

  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clientQ, setClientQ] = useState("");
  const [vehicleQ, setVehicleQ] = useState("");

  // Cliente
  const [clientId, setClientId] = useState(""); // existing
  const [newClient, setNewClient] = useState({ name: "", phone: "", email: "", address: "", tipo: "Particular", observacoes: "" });
  const [clientMode, setClientMode] = useState("existing"); // existing | new

  // Veículo
  const [vehicleId, setVehicleId] = useState("");
  const [newVehicle, setNewVehicle] = useState({ vin: "", plate: "", year: "", make: "", model: "", trim: "", engine: "", fuel: "Gasolina", drivetrain: "", mileage: "", photo_url: "" });
  const [vehicleMode, setVehicleMode] = useState("existing");
  const [uploading, setUploading] = useState(false);

  // Serviço
  const [service, setService] = useState({ service_type: "", technician: "", priority: "normal", notes: "", open_inspection: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);
  useEffect(() => {
    if (clientMode === "existing" && clientId) {
      api.get("/vehicles", { params: { client_id: clientId } }).then(({ data }) => setVehicles(data));
    } else {
      setVehicles([]);
    }
  }, [clientId, clientMode]);

  const filteredClients = useMemo(() => {
    const q = clientQ.trim().toLowerCase();
    if (!q) return clients.slice(0, 15);
    return clients.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    ).slice(0, 15);
  }, [clientQ, clients]);

  const filteredVehicles = useMemo(() => {
    const q = vehicleQ.trim().toLowerCase();
    if (!q) return vehicles.slice(0, 15);
    return vehicles.filter((v) =>
      (v.plate || "").toLowerCase().includes(q) ||
      (v.vin || "").toLowerCase().includes(q) ||
      (v.make || "").toLowerCase().includes(q) ||
      (v.model || "").toLowerCase().includes(q)
    ).slice(0, 15);
  }, [vehicleQ, vehicles]);

  const effectiveVehicleMode = clientMode === "new" ? "new" : vehicleMode;
  const canStep2 = clientMode === "existing" ? Boolean(clientId) : Boolean(newClient.name.trim());
  const canStep3 = effectiveVehicleMode === "existing" ? Boolean(vehicleId) : Boolean(newVehicle.make && newVehicle.model);
  const canSave = Boolean(service.service_type.trim());

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("aab_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}${token ? `?auth=${token}` : ""}`;
      setNewVehicle((v) => ({ ...v, photo_url: url }));
      toast.success("Foto enviada");
    } catch { toast.error("Erro no upload"); }
    finally { setUploading(false); }
    e.target.value = "";
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        service_type: service.service_type.trim(),
        technician: service.technician || null,
        priority: service.priority,
        notes: service.notes || null,
        job_type: jobType,
        extra: extra,
        open_inspection: service.open_inspection,
      };
      if (clientMode === "existing") payload.client_id = clientId;
      else payload.client = newClient;
      if (effectiveVehicleMode === "existing") payload.vehicle_id = vehicleId;
      else payload.vehicle = {
        ...newVehicle,
        year: newVehicle.year ? parseInt(newVehicle.year) : null,
        mileage: newVehicle.mileage ? parseInt(newVehicle.mileage) : null,
        client_id: "", // ignored; backend attaches
      };

      const { data } = await api.post("/checkin", payload);
      toast.success("Veículo recebido! Redirecionando…");
      if (data.inspection?.id) navigate(`/inspecoes/${data.inspection.id}`);
      else navigate("/kanban");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Erro ao fazer check-in");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="checkin-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Recepção</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Check-In</h1>
        <p className="text-sm text-muted-foreground mt-1">Registre a chegada do veículo à oficina em um único fluxo.</p>
      </div>

      {/* Tipo de Atendimento */}
      <div className="border border-border rounded-md bg-card p-4" data-testid="checkin-jobtype">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Tipo de atendimento</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {JOB_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setJobType(t.id);
                if (clientMode === "new" && !newClient.tipo) setNewClient((c) => ({ ...c, tipo: t.tipo }));
                setExtra({});
              }}
              className={`text-left border rounded-md p-3 transition-colors ${jobType === t.id ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-border hover:bg-accent/50"}`}
              data-testid={`jobtype-${t.id}`}
            >
              <t.icon className="w-4 h-4 text-primary mb-1" />
              <div className="font-semibold text-sm">{t.label}</div>
              <div className="text-[11px] text-muted-foreground">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>


      {/* Stepper */}
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider" data-testid="checkin-stepper">
        {[
          { n: 1, label: "Cliente", icon: User },
          { n: 2, label: "Veículo", icon: Car },
          { n: 3, label: "Serviço", icon: ClipboardList },
        ].map((s, i) => (
          <div key={s.n} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded border ${step >= s.n ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
              <s.icon className="w-3 h-3" />
              {s.n}. {s.label}
            </div>
            {i < 2 && <ArrowRight className="w-3 h-3 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {/* Step 1: Cliente */}
      {step === 1 && (
        <div className="border border-border rounded-md bg-card p-5 space-y-4" data-testid="checkin-step-client">
          <div className="flex gap-2">
            <Button variant={clientMode === "existing" ? "default" : "outline"} size="sm" onClick={() => setClientMode("existing")} data-testid="client-mode-existing">Cliente existente</Button>
            <Button variant={clientMode === "new" ? "default" : "outline"} size="sm" onClick={() => setClientMode("new")} data-testid="client-mode-new">Novo cliente</Button>
          </div>

          {clientMode === "existing" ? (
            <div className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Buscar por nome, telefone, email…" value={clientQ} onChange={(e) => setClientQ(e.target.value)} className="pl-9" data-testid="client-search-input" />
              </div>
              <div className="border border-border rounded-md divide-y divide-border max-h-72 overflow-y-auto">
                {filteredClients.length === 0 ? <div className="p-6 text-sm text-muted-foreground text-center">Nenhum cliente encontrado.</div> :
                  filteredClients.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setClientId(c.id)}
                      className={`w-full px-4 py-2.5 text-left flex items-center justify-between hover:bg-accent/50 ${clientId === c.id ? "bg-primary/10" : ""}`}
                      data-testid={`checkin-client-${c.id}`}
                    >
                      <div>
                        <div className="text-sm font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.phone || "—"} · {c.tipo}</div>
                      </div>
                      {clientId === c.id && <Check className="w-4 h-4 text-primary" />}
                    </button>
                  ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="checkin-new-client">
              <div>
                <Label>Nome *</Label>
                <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} data-testid="new-client-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Telefone</Label><Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} data-testid="new-client-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={newClient.tipo} onValueChange={(v) => setNewClient({ ...newClient, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Endereço</Label><Input value={newClient.address} onChange={(e) => setNewClient({ ...newClient, address: e.target.value })} /></div>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button disabled={!canStep2} onClick={() => setStep(2)} data-testid="checkin-next-1">Avançar <ArrowRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Step 2: Veículo */}
      {step === 2 && (
        <div className="border border-border rounded-md bg-card p-5 space-y-4" data-testid="checkin-step-vehicle">
          {clientMode === "existing" && (
            <div className="flex gap-2">
              <Button variant={vehicleMode === "existing" ? "default" : "outline"} size="sm" onClick={() => setVehicleMode("existing")} data-testid="vehicle-mode-existing">Veículo do cliente</Button>
              <Button variant={vehicleMode === "new" ? "default" : "outline"} size="sm" onClick={() => setVehicleMode("new")} data-testid="vehicle-mode-new">Novo veículo</Button>
            </div>
          )}

          {clientMode === "existing" && vehicleMode === "existing" ? (
            <div className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Buscar por placa, VIN, marca…" value={vehicleQ} onChange={(e) => setVehicleQ(e.target.value)} className="pl-9" data-testid="vehicle-search-input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                {filteredVehicles.length === 0 ? <div className="col-span-2 p-6 text-sm text-muted-foreground text-center border border-dashed border-border rounded">Este cliente ainda não possui veículos. Use "Novo veículo".</div> :
                  filteredVehicles.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setVehicleId(v.id)}
                      className={`text-left border rounded-md overflow-hidden hover:-translate-y-0.5 transition-transform ${vehicleId === v.id ? "border-primary ring-2 ring-primary/30" : "border-border"}`}
                      data-testid={`checkin-vehicle-${v.id}`}
                    >
                      <div className="h-20 bg-secondary">
                        {v.photo_url ? <img src={v.photo_url} alt="" className="w-full h-full object-cover" /> :
                          <div className="w-full h-full flex items-center justify-center"><Car className="w-6 h-6 text-muted-foreground" /></div>}
                      </div>
                      <div className="p-2">
                        <div className="font-display font-semibold text-sm">{v.year} {v.make} {v.model}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{v.plate || "—"}</div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="checkin-new-vehicle">
              <div className="border border-border rounded p-3 bg-secondary/30">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Escanear VIN (parabrisa ou etiqueta da porta)</div>
                <VinScanner
                  value={newVehicle.vin}
                  onChange={(v) => setNewVehicle((prev) => ({ ...prev, vin: v }))}
                  onDecoded={(d) => {
                    setNewVehicle((prev) => ({
                      ...prev,
                      vin: d.vin,
                      year: d.year || prev.year,
                      make: d.make || prev.make,
                      model: d.model || prev.model,
                      trim: d.trim || prev.trim,
                      engine: d.engine || prev.engine,
                      fuel: d.fuel || prev.fuel,
                      drivetrain: d.drivetrain || prev.drivetrain,
                    }));
                    toast.success("Dados preenchidos automaticamente");
                  }}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Ano</Label><Input type="number" value={newVehicle.year} onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })} /></div>
                <div><Label>Marca *</Label><Input value={newVehicle.make} onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })} data-testid="new-vehicle-make" /></div>
                <div><Label>Modelo *</Label><Input value={newVehicle.model} onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })} data-testid="new-vehicle-model" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Placa</Label><Input value={newVehicle.plate} onChange={(e) => setNewVehicle({ ...newVehicle, plate: e.target.value })} data-testid="new-vehicle-plate" /></div>
                <div><Label>Milhagem (Mileage)</Label><Input type="number" value={newVehicle.mileage} onChange={(e) => setNewVehicle({ ...newVehicle, mileage: e.target.value })} /></div>
              </div>
              <div>
                <Label>Foto</Label>
                <div className="flex gap-2">
                  <Input value={newVehicle.photo_url} onChange={(e) => setNewVehicle({ ...newVehicle, photo_url: e.target.value })} placeholder="https://… ou upload" />
                  <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-input hover:bg-accent cursor-pointer text-sm">
                    <Upload className="w-3.5 h-3.5" />
                    {uploading ? "…" : "Upload"}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                  </label>
                </div>
                {newVehicle.photo_url && <img src={newVehicle.photo_url} alt="" className="mt-2 h-24 rounded border border-border object-cover" />}
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)}>Voltar</Button>
            <Button disabled={!canStep3} onClick={() => setStep(3)} data-testid="checkin-next-2">Avançar <ArrowRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Step 3: Serviço */}
      {step === 3 && (
        <div className="border border-border rounded-md bg-card p-5 space-y-4" data-testid="checkin-step-service">
          <div>
            <Label>Tipo de Serviço *</Label>
            <Input value={service.service_type} onChange={(e) => setService({ ...service, service_type: e.target.value })} placeholder="Ex: Barulho na suspensão dianteira" data-testid="service-type-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Técnico Responsável</Label>
              <Input value={service.technician} onChange={(e) => setService({ ...service, technician: e.target.value })} />
            </div>
            <div>
              <Label>Prioridade</Label>
              <Select value={service.priority} onValueChange={(v) => setService({ ...service, priority: v })}>
                <SelectTrigger data-testid="service-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                  <SelectItem value="importante">Importante</SelectItem>
                  <SelectItem value="monitorar">Monitorar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notas iniciais</Label>
            <Textarea rows={3} value={service.notes} onChange={(e) => setService({ ...service, notes: e.target.value })} placeholder="Relato do cliente, sintomas, condições…" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="open-inspection-toggle">
            <input
              type="checkbox"
              checked={service.open_inspection}
              onChange={(e) => setService({ ...service, open_inspection: e.target.checked })}
              className="w-4 h-4 accent-primary"
            />
            Abrir inspeção imediatamente após check-in
          </label>

          {/* Type-specific extra fields */}
          {jobType === "body_shop" && (
            <div className="border-t border-border pt-4 mt-4 space-y-3" data-testid="extra-body-shop">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Body Shop · Dados da seguradora</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Seguradora</Label>
                  <Input value={extra.seguradora || ""} onChange={(e) => setExtra({ ...extra, seguradora: e.target.value })} placeholder="Ex: State Farm" data-testid="extra-seguradora" />
                </div>
                <div>
                  <Label>Valor Aprovado ($)</Label>
                  <Input type="number" step="0.01" value={extra.valor_aprovado || ""} onChange={(e) => setExtra({ ...extra, valor_aprovado: Number(e.target.value) || 0 })} data-testid="extra-valor-aprovado" />
                </div>
              </div>
            </div>
          )}

          {jobType === "warranty" && (
            <div className="border-t border-border pt-4 mt-4 space-y-3" data-testid="extra-warranty">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Garantia Estendida · Dados do claim</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Empresa (warranty)</Label>
                  <Input value={extra.empresa || ""} onChange={(e) => setExtra({ ...extra, empresa: e.target.value })} placeholder="Ex: MaxCare" data-testid="extra-empresa" />
                </div>
                <div>
                  <Label>Claim #</Label>
                  <Input value={extra.claim || ""} onChange={(e) => setExtra({ ...extra, claim: e.target.value })} className="font-mono" data-testid="extra-claim" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Deductible ($)</Label>
                  <Input type="number" step="0.01" value={extra.deductible ?? ""} onChange={(e) => setExtra({ ...extra, deductible: Number(e.target.value) || 0 })} data-testid="extra-deductible" />
                </div>
                <div>
                  <Label>Labor Rate ($/h)</Label>
                  <Input type="number" step="0.01" value={extra.labor_rate ?? ""} onChange={(e) => setExtra({ ...extra, labor_rate: Number(e.target.value) || 0 })} data-testid="extra-labor-rate" />
                </div>
                <div>
                  <Label>Inspector</Label>
                  <Input value={extra.inspector || ""} onChange={(e) => setExtra({ ...extra, inspector: e.target.value })} data-testid="extra-inspector" />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="ghost" onClick={() => setStep(2)}>Voltar</Button>
            <Button disabled={!canSave || saving} onClick={submit} data-testid="checkin-submit-btn">
              <LogIn className="w-4 h-4" />
              {saving ? "Registrando…" : "Fazer Check-In"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
